import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  styled,
} from "@mui/material";
import { DeleteOutline, EditOutlined, VisibilityOutlined, ShareOutlined } from "@mui/icons-material";
import {
  ArrowRight,
  Clapperboard,
  FileText,
  Globe,
} from "lucide-react";

// Types
import { SeriesSerializer, SeriesType, Days } from "../types/MeGenTypes";

export type SeriesItemProps = {
  series: SeriesSerializer;
  isActive?: boolean;
  onView?: (series: SeriesSerializer) => void;
  onSelect?: (series: SeriesSerializer) => void;
  onEdit?: (series: SeriesSerializer) => void; // เพิ่ม
  onDelete?: (series: SeriesSerializer) => void;
  onShare?: (series: SeriesSerializer) => void;
};

// Consts
const SERIES_TYPE_COLORS: Record<SeriesType, string> = {
  story2scene: "#fce4ec",
  rss_news: "#fce4ec",
  script2vdo: "#fce4ec",
} as const;

// Helper Functions
const formatDays = (days: Days[]): string => {
  if (!days || days.length === 0) return "";

  const dayMap: Record<Days, string> = {
    mon: "M",
    tue: "T",
    wed: "W",
    thu: "T",
    fri: "F",
    sat: "S",
    sun: "S",
  };

  return days.map((day) => dayMap[day]).join(", ");
};

const formatTime = (hour: number, minute: number): string => {
  const h = hour.toString().padStart(2, "0");
  const m = minute.toString().padStart(2, "0");
  return `${h}:${m}`;
};

// Styled Components
const SeriesContainer = styled(Box)<{ isActive?: boolean }>(({ isActive }) => ({
  display: "flex",
  alignItems: "center",
  padding: "10px 12px",
  backgroundColor: isActive ? "#fff1f3" : "white",
  border: `1px solid ${isActive ? "#f9a8d4" : "#f1f5f9"}`,
  borderRadius: "0.75rem",
  marginBottom: "0.5rem",
  transition: "all 0.2s ease",
  cursor: "pointer",
  gap: "0.75rem",
  ...(isActive && {
    boxShadow: "0 2px 8px rgba(233, 30, 99, 0.12)",
    "& .action-buttons": {
      opacity: 1,
    },
  }),
  "&:hover": {
    borderColor: "#fce4ec",
    backgroundColor: isActive ? "#fff1f3" : "#fffbfc",
    boxShadow: "0 2px 8px rgba(233, 30, 99, 0.08)",
    "& .action-buttons": {
      opacity: 1,
    },
  },
}));

const IconWrapper = styled(Box)<{ type: SeriesType }>(({ type }) => ({
  width: 36,
  height: 36,
  borderRadius: "50%",
  backgroundColor: SERIES_TYPE_COLORS[type] || "#fce4ec",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const ContentSection = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

const SeriesName = styled(Typography)({
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "#1e293b",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const SeriesSubInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  flexWrap: "wrap",
});

const InfoTag = styled(Typography)({
  fontSize: "0.7rem",
  color: "#94a3b8",
  display: "flex",
  alignItems: "center",
  gap: "2px",
});

const AutoBadge = styled(Box)<{ active: boolean }>(({ active }) => ({
  width: "55px",
  fontSize: "0.65rem",
  fontWeight: 500,
  textAlign: "center",
  padding: "2px 6px",
  borderRadius: "4px",
  backgroundColor: active ? "#dcfce7" : "#f1f5f9",
  color: active ? "#16a34a" : "#94a3b8",
}));

const ActionButtons = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "2px",
  opacity: 0,
  transition: "opacity 0.2s ease",
  flexShrink: 0,
});

const EditButton = styled(IconButton)({
  padding: "4px",
  color: "#94a3b8",
  "&:hover": {
    backgroundColor: "#e0f2fe",
    color: "#0284c7",
  },
});

const DeleteButton = styled(IconButton)({
  padding: "4px",
  color: "#94a3b8",
  "&:hover": {
    backgroundColor: "#fef2f2",
    color: "#ef4444",
  },
});

const ViewButton = styled(IconButton)({
  padding: "4px",
  color: "#64748b",
  "&:hover": {
    backgroundColor: "#f1f5f9",
    color: "#475569"
  },
});

const ArrowButton = styled(IconButton)({
  padding: "0.375rem",
  color: "#cbd5e1",
  transition: "all 0.2s",
  "&:hover": {
    color: "#f382a0",
    transform: "translateX(4px)",
  },
});

const RenderSeriesType = ({ type }: { type: SeriesType }) => {
  switch (type) {
    case "story2scene":
      return <FileText size={18} color={"#e91e63"} />;
    case "script2vdo":
      return <Clapperboard size={18} color={"#e91e63"} />;
    case "rss_news":
    default:
      return <Globe size={18} color={"#e91e63"} />;
  }
};

const SeriesItem: React.FC<SeriesItemProps> = ({
  series,
  isActive,
  onView,
  onSelect,
  onEdit,
  onDelete,
  onShare,
}) => {
  const scheduleConfig = series.schedule_config;
  const hasCron = scheduleConfig && "cron" in scheduleConfig && scheduleConfig.cron;
  const isAutoGenerate = hasCron && scheduleConfig.cron?.enabled;

  const getScheduleInfo = (): string => {
    if (!isAutoGenerate || !scheduleConfig.cron) return "";
    
    const cron = scheduleConfig.cron;
    const days = formatDays(cron.days);
    const time = formatTime(cron.hour, cron.minute);
    return `${days} Runtime: ${time}`;
  };

  const handleClick = () => {
    if (series.id) {
      onSelect?.(series);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (series.id) {
      onEdit?.(series);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (series.id) {
      onDelete?.(series);
    }
  };

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (series.id) {
      onView?.(series);
    }
  };

  const handleEpisodeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (series.id) {
      onSelect?.(series);
    }
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (series.id) {
      onShare?.(series);
    }
  };

  const scheduleInfo = getScheduleInfo();

  return (
    <SeriesContainer id={`megen-series-item-${series.id}-container`} onClick={handleClick} isActive={isActive}>
      {/* Icon */}
      <IconWrapper id={`megen-series-item-${series.id}-icon`} type={series.series_type} >
        <RenderSeriesType type={series.series_type} />
      </IconWrapper>

      {/* Content */}
      <ContentSection id={`megen-series-item-${series.id}-content`}>
        <SeriesName id={`megen-series-item-${series.id}-name`} title={series.name}>{series.name}</SeriesName>
        <SeriesSubInfo>
          <AutoBadge active={isAutoGenerate || false}>
            {isAutoGenerate ? "Auto" : "Non Auto"}
          </AutoBadge>
          {scheduleInfo && (
            <InfoTag>{scheduleInfo}</InfoTag>
          )}
        </SeriesSubInfo>
      </ContentSection>

      {/* Action Buttons */}
      <ActionButtons id={`megen-series-item-${series.id}-actions`} className="action-buttons">
        <Tooltip title="Share" placement="top">
          <EditButton id={`megen-series-item-${series.id}-share-btn`} size="small" onClick={handleShareClick}>
            <ShareOutlined sx={{ fontSize: 16 }} />
          </EditButton>
        </Tooltip>
        <Tooltip title="Edit" placement="top">
          <EditButton id={`megen-series-item-${series.id}-edit-btn`} size="small" onClick={handleEditClick}>
            <EditOutlined sx={{ fontSize: 16 }} />
          </EditButton>
        </Tooltip>
        <Tooltip title="Delete" placement="top">
          <DeleteButton id={`megen-series-item-${series.id}-delete-btn`} size="small" onClick={handleDeleteClick}>
            <DeleteOutline sx={{ fontSize: 16 }} />
          </DeleteButton>
        </Tooltip>
        <Tooltip title="View Details" placement="top">
          <ViewButton id={`megen-series-item-${series.id}-view-btn`} size="small" onClick={handleViewClick}>
            <VisibilityOutlined sx={{ fontSize: 16 }} />
          </ViewButton>
        </Tooltip>
        <Tooltip title="View Episodes" placement="top">
          <ArrowButton id={`megen-series-item-${series.id}-episodes-btn`} size="small" onClick={handleEpisodeClick}>
            <ArrowRight size={16} />
          </ArrowButton>
        </Tooltip>
      </ActionButtons>
    </SeriesContainer>
  );
};

export default SeriesItem;